/**
 * PaymentsListView Organism
 * Pure presentation component for payments list with configurable grouping.
 * Supports three modes: flat (none), by pot reference, and by gang/solo.
 */

import { useCallback, useMemo } from 'react';
import {
  Alert,
  Collapse,
  LedgerHeader,
  PaymentsIcon,
  ledgerContainerClass,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { useShallow } from 'zustand/react/shallow';
import { PaymentListItem } from './PaymentListItem';
import { PaymentGroupHeader } from './PaymentGroupHeader';
import { ListViewShell } from '../../../shared/components/ui/ListViewShell';
import {
  groupPaymentsByPot,
  groupPaymentsByPotReference,
} from '../utils/groupPaymentsByPot';
import { aggregatePaymentsByWorker } from '../utils/groupPaymentsByWorker';
import { usePaymentsUIStore } from '../stores/paymentsUIStore';
import type { PaymentGroupingMode } from '../stores/paymentsUIStore';
import type { PaymentRowDto } from '../api/paymentsApi';

export interface PaymentsListViewProps {
  payments: PaymentRowDto[];
  isLoading: boolean;
  error: Error | null;
  showBankDetails: boolean;
  onPaymentClick?: (payment: PaymentRowDto) => void;
}

const PAYMENTS_COLLAPSE: LedgerCollapse = {
  container: 'payments-collection',
  at: 620,
};

const PAYMENTS_COLUMNS: LedgerColumn[] = [
  { key: 'status', label: 'Status', flex: 0.8 },
  { key: 'account', label: 'Account', flex: 0.9 },
  { key: 'sortCode', label: 'Sort code', flex: 0.8, hideBelow: 760 },
];

export function PaymentsListView({
  payments,
  isLoading,
  error,
  showBankDetails,
  onPaymentClick,
}: PaymentsListViewProps) {
  const { collapsedGroups, toggleGroup, groupingMode } = usePaymentsUIStore(
    useShallow(state => ({
      collapsedGroups: state.collapsedGroups,
      toggleGroup: state.toggleGroup,
      groupingMode: state.groupingMode,
    }))
  );

  const handleToggle = useCallback(
    (groupName: string) => () => toggleGroup(groupName),
    [toggleGroup]
  );

  if (error && !isLoading && payments.length === 0) {
    return (
      <div className="p-4">
        <Alert severity="error">
          Failed to load payments. Please try again.
        </Alert>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full min-h-0 flex-col overflow-auto ${ledgerContainerClass(PAYMENTS_COLLAPSE.container)}`}
    >
      <ListViewShell
        itemCount={payments.length}
        loading={isLoading}
        emptyIcon={
          <PaymentsIcon
            aria-hidden="true"
            className="size-12 text-content-secondary"
          />
        }
        emptyTitle="No payments this week"
        emptyHelperText="Payment breakdowns appear here after pots are approved. Each worker's payout is shown individually."
        emptyMessage=""
        data-testid="payments-list"
      >
        <LedgerHeader
          columns={PAYMENTS_COLUMNS}
          collapse={PAYMENTS_COLLAPSE}
          titleLabel="Name"
          valueLabel="Amount"
          showActions
          hideBelow={PAYMENTS_COLLAPSE.at}
        />
        {groupingMode === 'none' ? (
          <FlatPaymentsList
            payments={payments}
            columns={PAYMENTS_COLUMNS}
            collapse={PAYMENTS_COLLAPSE}
            showBankDetails={showBankDetails}
            onPaymentClick={onPaymentClick}
          />
        ) : (
          <GroupedPaymentsList
            payments={payments}
            columns={PAYMENTS_COLUMNS}
            collapse={PAYMENTS_COLLAPSE}
            groupingMode={groupingMode}
            collapsedGroups={collapsedGroups}
            showBankDetails={showBankDetails}
            onToggle={handleToggle}
            onPaymentClick={onPaymentClick}
          />
        )}
      </ListViewShell>
    </div>
  );
}

function FlatPaymentsList({
  payments,
  columns,
  collapse,
  showBankDetails,
  onPaymentClick,
}: {
  payments: PaymentRowDto[];
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  showBankDetails: boolean;
  onPaymentClick?: (payment: PaymentRowDto) => void;
}) {
  const aggregated = useMemo(
    () => aggregatePaymentsByWorker(payments),
    [payments]
  );

  return (
    <>
      {aggregated.map((payment, index) => (
        <PaymentListItem
          key={`${payment.personName}-${payment.accountNumber}-${index}`}
          payment={payment}
          columns={columns}
          collapse={collapse}
          showBankDetails={showBankDetails}
          onClick={() => onPaymentClick?.(payment)}
          data-testid={`payment-row-${index}`}
        />
      ))}
    </>
  );
}

function GroupedPaymentsList({
  payments,
  columns,
  collapse,
  groupingMode,
  collapsedGroups,
  showBankDetails,
  onToggle,
  onPaymentClick,
}: {
  payments: PaymentRowDto[];
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  groupingMode: Exclude<PaymentGroupingMode, 'none'>;
  collapsedGroups: Set<string>;
  showBankDetails: boolean;
  onToggle: (groupName: string) => () => void;
  onPaymentClick?: (payment: PaymentRowDto) => void;
}) {
  const groups = useMemo(
    () =>
      groupingMode === 'pot'
        ? groupPaymentsByPotReference(payments)
        : groupPaymentsByPot(payments),
    [payments, groupingMode]
  );

  return (
    <>
      {groups.map((group, index) => {
        const isCollapsed = collapsedGroups.has(group.groupName);
        const panelId = `payment-group-panel-${index}`;
        return (
          <div key={group.groupName}>
            <PaymentGroupHeader
              groupName={group.groupName}
              count={group.payments.length}
              totalAmount={group.totalAmount}
              expanded={!isCollapsed}
              onToggle={onToggle(group.groupName)}
              collapse={collapse}
              panelId={panelId}
              data-testid={`payment-group-${index}`}
            />
            <Collapse in={!isCollapsed} unmountOnExit>
              <div id={panelId}>
                {group.payments.map((payment, index) => (
                  <PaymentListItem
                    key={`${payment.potReference}-${payment.personName}-${index}`}
                    payment={payment}
                    columns={columns}
                    collapse={collapse}
                    showBankDetails={showBankDetails}
                    onClick={() => onPaymentClick?.(payment)}
                    data-testid={`payment-row-${index}`}
                  />
                ))}
              </div>
            </Collapse>
          </div>
        );
      })}
    </>
  );
}
